import { Popup } from "@progress/kendo-react-popup";
import { AttendanceStatus } from "../../../data/entities";
import { AppointmentMenuState, ParkedAppointment } from "../hooks/useAppointmentClipboard";
import "./AppointmentContextMenu.scss";

interface AppointmentContextMenuProps {
  menu: AppointmentMenuState | null;
  menuRef: React.MutableRefObject<HTMLDivElement | null>;
  parked: ParkedAppointment | null;
  onCopy: () => void;
  onCut: () => void;
  onPlace: () => void;
  onSetAttendance?: (dataItem: any, status: AttendanceStatus) => void;
}

export const AppointmentContextMenu = (props: AppointmentContextMenuProps) => {
  const { menu, menuRef, parked, onCopy, onCut, onPlace, onSetAttendance } = props;

  if (!menu) {
    return null;
  }

  const dataItem = menu.dataItem;
  const attendanceStatus: AttendanceStatus = dataItem?.attendanceStatus ?? AttendanceStatus.Pending;
  const hasStarted = dataItem?.start ? new Date(dataItem.start).getTime() <= Date.now() : false;
  const isCheckedIn = attendanceStatus === AttendanceStatus.CheckedIn;

  return (
    <Popup
      show={true}
      offset={menu.offset}
      animate={false}
      style={{ zIndex: 10010 }}
      popupClass="bg-transparent border-0 shadow-none"
    >
      <div className="appointment-context-menu dropdown-menu show position-static shadow overflow-hidden px-2" ref={menuRef}>
        {menu.mode === "item" ? (
          <>
            <button
              type="button"
              className="dropdown-item d-flex align-items-center gap-2"
              onClick={onCut}
              disabled={isCheckedIn}
              title={isCheckedIn ? "This appointment is already checked in" : undefined}
            >
              <i className="fa fa-cut" />
              Move appointment
            </button>
            <button
              type="button"
              className="dropdown-item d-flex align-items-center gap-2"
              onClick={onCopy}
            >
              <i className="fa fa-copy" />
              Copy appointment
            </button>
            {onSetAttendance && (
              <>
                <hr className="dropdown-divider" />
                {attendanceStatus === AttendanceStatus.Pending && (
                  <>
                    <button
                      type="button"
                      className="dropdown-item d-flex align-items-center gap-2"
                      onClick={() => onSetAttendance(dataItem, AttendanceStatus.CheckedIn)}
                    >
                      <i className="fa fa-check" />
                      Mark as checked in
                    </button>
                    <button
                      type="button"
                      className="dropdown-item d-flex align-items-center gap-2"
                      onClick={() => onSetAttendance(dataItem, AttendanceStatus.NoShow)}
                      disabled={!hasStarted}
                      title={hasStarted ? undefined : "Available once the appointment start time has passed"}
                    >
                      <i className="fa fa-ban" />
                      Mark as no-show
                    </button>
                  </>
                )}
                {attendanceStatus === AttendanceStatus.CheckedIn && (
                  <button
                    type="button"
                    className="dropdown-item d-flex align-items-center gap-2"
                    onClick={() => onSetAttendance(dataItem, AttendanceStatus.Pending)}
                  >
                    <i className="fa fa-undo" />
                    Undo check-in
                  </button>
                )}
                {attendanceStatus === AttendanceStatus.NoShow && (
                  <button
                    type="button"
                    className="dropdown-item d-flex align-items-center gap-2"
                    onClick={() => onSetAttendance(dataItem, AttendanceStatus.Pending)}
                  >
                    <i className="fa fa-undo" />
                    Undo no-show
                  </button>
                )}
              </>
            )}
          </>
        ) : (
          <>
            <button
              type="button"
              className="dropdown-item d-flex align-items-center gap-2"
              onClick={onPlace}
              disabled={!parked}
            >
              <i className={`fa ${parked?.mode === "cut" ? "fa-arrow-right" : "fa-paste"}`} />
              {parked?.mode === "cut" ? "Move here" : "Paste a copy here"}
            </button>
            <hr className="dropdown-divider" />
            <div className="dropdown-header text-wrap py-0" style={{ maxWidth: 260 }}>
              {parked
                ? parked.label
                : "Right-click an appointment and move or copy it first"}
            </div>
          </>
        )}
      </div>
    </Popup>
  );
};

export default AppointmentContextMenu;
